import { Box, Grid, Stack, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { displaySum } from "../../common/formatting/displaySum";
import { HorizontalScrollBox } from "../../common/HorizontalScrollBox";
import { useRestoreScrollLeftOnBack } from "../../common/layout/useRestoreScrollOnBack";
import { useProcurementTerms } from "../../common/terminology/useProcurementTerms";
import { PlanEntryCard } from "./PlanEntryCard";
import {
  BOARD_COLUMNS,
  byBoardColumn,
  planTotals,
  type BoardColumn,
  type PlanEntry,
} from "./procurementPlanEntries";

const COLUMN_WIDTH = "11rem";
const COLUMNS_TEMPLATE = `repeat(${BOARD_COLUMNS.length}, minmax(${COLUMN_WIDTH}, 1fr))`;

const useSameScrollLeft = (first: HTMLElement | null, second: HTMLElement | null) => {
  useEffect(() => {
    if (!first || !second) return;
    const follow = (from: HTMLElement, to: HTMLElement) => () => {
      to.scrollLeft = from.scrollLeft;
    };
    const secondFollows = follow(first, second);
    const firstFollows = follow(second, first);
    first.addEventListener("scroll", secondFollows, { passive: true });
    second.addEventListener("scroll", firstFollows, { passive: true });
    return () => {
      first.removeEventListener("scroll", secondFollows);
      second.removeEventListener("scroll", firstFollows);
    };
  }, [first, second]);
};

export const PlanBoard = ({ entries }: { entries: PlanEntry[] }) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const [headings, setHeadings] = useState<HTMLElement | null>(null);
  const [cards, setCards] = useState<HTMLElement | null>(null);
  useSameScrollLeft(headings, cards);
  useRestoreScrollLeftOnBack(cards);
  const columns = byBoardColumn(entries);
  const titles: Record<BoardColumn, string> = {
    withProcurer: t("With procurement"),
    planned: t("Planned"),
    draft: t("Draft"),
    published: terms.publishedStatus,
    evaluation: t("Evaluation and negotiation"),
  };
  return (
    <Box data-testid="plan-board">
      <HorizontalScrollBox
        position="sticky"
        top="0"
        zIndex="1"
        bg="smBackgroundSecondary"
        borderBottomWidth="1px"
        borderColor="smBorder"
        scrollerRef={setHeadings}
        data-testid="plan-board-headings"
      >
        <Grid templateColumns={COLUMNS_TEMPLATE} columnGap="3" rowGap="2" pb="2">
          <GroupHeading span={2}>{t("Needs without a procurement")}</GroupHeading>
          <GroupHeading span={3}>{t("Procurements")}</GroupHeading>
          {BOARD_COLUMNS.map((column) => (
            <ColumnHeading key={column} column={column} title={titles[column]} entries={columns[column]} />
          ))}
        </Grid>
      </HorizontalScrollBox>
      <HorizontalScrollBox pb="2" scrollerRef={setCards} data-testid="plan-board-cards">
        <Grid templateColumns={COLUMNS_TEMPLATE} columnGap="3">
          {BOARD_COLUMNS.map((column) => (
            <Stack
              key={column}
              as="section"
              aria-label={titles[column]}
              spacing="2"
              bg="smBackgroundSecondary"
              borderRadius="md"
              px="2"
              py="2"
              minW="0"
              data-testid={`plan-column-${column}`}
            >
              {columns[column].map((entry) => (
                <PlanEntryCard key={entry.key} entry={entry} variant="board" />
              ))}
            </Stack>
          ))}
        </Grid>
      </HorizontalScrollBox>
    </Box>
  );
};

const GroupHeading = ({ span, children }: { span: number; children: string }) => (
  <Text
    gridColumn={`span ${span}`}
    fontSize="xs"
    fontWeight="semibold"
    textTransform="uppercase"
    color="smMuted"
    borderBottomWidth="1px"
    borderColor="smBorder"
    pb="1"
  >
    {children}
  </Text>
);

const ColumnHeading = ({ column, title, entries }: { column: BoardColumn; title: string; entries: PlanEntry[] }) => {
  const { t } = useTranslation();
  return (
    <Box px="3" py="1" minW="0" data-testid={`plan-column-${column}-heading`}>
      <Text fontWeight="semibold" color="smText">
        {title}
      </Text>
      <Text fontSize="sm" color="smMuted" data-testid={`plan-column-${column}-total`}>
        {t("{{count}} entries · {{sum}}", {
          count: entries.length,
          sum: withCurrencyKeptOnTheLine(displaySum({ value: planTotals(entries).sum, currency: "NOK" })),
        })}
      </Text>
    </Box>
  );
};

const withCurrencyKeptOnTheLine = (sum: string) => sum.replace(/ (?=\S+$)/, "\u00a0");
